import { Store, Package, ShoppingCart, Settings, LayoutDashboard, Users, Percent, BarChart3, FileCheck, Activity, Upload, Webhook, Shield, UserCog, KeyRound, Command, Gauge, Warehouse, MonitorCog } from "lucide-react";
import { memo, useMemo, useCallback } from "react";

import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";

const mainNavItems = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard, roles: ['admin', 'partner', 'customer'] },
  { title: "Nettbutikker", url: "/webshops", icon: Store, roles: ['admin', 'partner'] },
  { title: "Produkter", url: "/products", icon: Package, roles: ['admin', 'partner', 'customer'] },
  { title: "Lagre", url: "/warehouses", icon: Warehouse, roles: ['admin', 'partner', 'customer'] },
  { title: "Ordrer", url: "/orders", icon: ShoppingCart, roles: ['admin', 'partner', 'customer'] },
  { title: "Kunder", url: "/customers", icon: Users, roles: ['admin', 'partner', 'customer'] },
  { title: "Rabatter", url: "/discounts", icon: Percent, roles: ['admin', 'partner', 'customer'] },
];

const adminNavItems = [
  { title: "Brukere", url: "/users", icon: Shield, roles: ['admin'] },
  { title: "Partnere", url: "/partners", icon: UserCog, roles: ['admin'] },
  { title: "Aktivitetslogg", url: "/activity-log", icon: Activity, roles: ['admin'] },
  { title: "Performance", url: "/performance-monitor", icon: Gauge, roles: ['admin'] },
  { title: "PCKasse Monitor", url: "/pckasse-monitor", icon: MonitorCog, roles: ['admin', 'partner'] },
  { title: "Bulk Operasjoner", url: "/bulk-operations", icon: Upload, roles: ['admin'] },
  { title: "Webhook", url: "/webhook-settings", icon: Webhook, roles: ['admin'] },
];

const settingsNavItems = [
  { title: "Kredittsøknader", url: "/credit-applications", icon: FileCheck, roles: ['admin', 'partner', 'customer'] },
  { title: "Rapporter", url: "/reports", icon: BarChart3, roles: ['admin', 'partner', 'customer'] },
  { title: "Brukertilganger", url: "/user-access", icon: KeyRound, roles: ['admin', 'partner'] },
  { title: "Mine ansatte", url: "/my-users", icon: Users, roles: ['admin', 'partner'] },
  { title: "Mine kunder", url: "/my-customers", icon: Users, roles: ['admin', 'partner'] },
  { title: "Innstillinger", url: "/settings", icon: Settings, roles: ['admin', 'partner', 'customer'] },
];

export const AppSidebar = memo(function AppSidebar() {
  const { signOut, user, userRole } = useAuth();

  // Memoize filtered navigation items to prevent unnecessary recalculations
  const filteredMainNav = useMemo(() => 
    mainNavItems.filter(item => userRole && item.roles.includes(userRole)),
    [userRole]
  );

  const filteredAdminNav = useMemo(() => 
    adminNavItems.filter(item => userRole && item.roles.includes(userRole)),
    [userRole]
  );

  const filteredSettingsNav = useMemo(() => 
    settingsNavItems.filter(item => userRole && item.roles.includes(userRole)),
    [userRole]
  );

  // Memoize user object to prevent unnecessary re-renders
  const userInfo = useMemo(() => ({
    name: user?.user_metadata?.full_name || null,
    email: user?.email || null,
    role: userRole,
  }), [user?.user_metadata?.full_name, user?.email, userRole]);

  // Memoize signOut callback
  const handleSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/">
                <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <Command className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">PCKasse</span>
                  <span className="truncate text-xs">Nettbutikkstyring</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={filteredMainNav} label="Hovedmeny" />
        {filteredAdminNav.length > 0 && (
          <NavMain items={filteredAdminNav} label="Administrasjon" />
        )}
        <NavMain items={filteredSettingsNav} label="Innstillinger" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser 
          user={userInfo}
          onSignOut={handleSignOut}
        />
      </SidebarFooter>
    </Sidebar>
  );
});
